/** Pure split shares, derived balances and settlement (INV-1, INV-7, W-1). */
import { exponentOf, isCurrencyCode } from "../../shared/currencies";
import { validateSplit, validateTransfer } from "../../shared/split_rules";
import type { SplitEntry, SplitMode } from "../../shared/split_rules";
import { convertMinor, distributeLargestRemainder, formatMinor, minor, minorAsCents } from "./money";
import type { Cents, Minor, RateNano } from "./money";

import { assertSplitOccurredAt, SplitRowError } from "../../shared/split_timestamp";
export { assertSplitOccurredAt, SplitRowError } from "../../shared/split_timestamp";

const HOME_CURRENCY_EXPONENT = 2;

/** Structural inputs; persisted rows can add identity and sync metadata in the storage layer. */
export interface SplitGroupInput {
  name: string;
  baseCurrency: string;
  members: readonly { id: string; name: string }[];
}

export interface SplitExpenseInput {
  id?: string;
  occurredAt?: string;
  amountMinor: number;
  currency: string;
  /** One native major unit buys rateNano / 1e9 base major units. */
  rateNano: RateNano;
  payerMemberId: string;
  mode: SplitMode;
  entries: readonly SplitEntry[];
}

export interface SplitSummaryExpenseInput extends SplitExpenseInput {
  description: string;
  occurredAt: string;
}

export interface SplitTransferInput {
  id?: string;
  occurredAt?: string;
  from: string;
  to: string;
  /** Base-currency minor units. */
  amountMinor: number;
}

export interface SettlementTransfer extends SplitTransferInput {
  amountMinor: Minor;
}

/** Money constructors report validation with plain Error; other failures are programming errors. */
function rowMoney<T>(compute: () => T): T {
  try { return compute(); }
  catch (cause) {
    if (!(cause instanceof Error) || cause.constructor !== Error) throw cause;
    throw new SplitRowError(cause.message.startsWith("Unsupported currency") ? `${cause.message}. Choose a supported currency in the split or Settings before importing your share.` : cause.message, { cause });
  }
}

function rowMinor(value: number): Minor {
  return rowMoney(() => minor(value));
}

function rowConversion(amount: Minor, from: string, to: string, rate: RateNano): Minor {
  return rowMoney(() => convertMinor(amount, from, to, rate));
}

export class MissingHomeRateError extends Error {
  readonly code = "missing_home_rate";
  readonly from: string;
  readonly to: string;

  constructor(from: string, to: string) {
    super(`Enter a ${from} to ${to} rate to import your share.`);
    this.name = "MissingHomeRateError";
    this.from = from;
    this.to = to;
  }
}

function assertSplit(expense: Pick<SplitExpenseInput, "amountMinor" | "currency" | "mode" | "entries">, memberIds: readonly string[]): void {
  const result = validateSplit({ ...expense, memberIds });
  if (!result.ok) throw new SplitRowError(result.reason === "invalid_currency" ? `${result.detail} Choose a supported currency in the split before importing your share.` : `${result.reason}: ${result.detail}`);
}

/** Entries determine iteration order and break tied allocation remainders. */
export function computeShares(amountMinor: number, mode: SplitMode, entries: readonly SplitEntry[], currency: string): Map<string, Minor> {
  assertSplit({ amountMinor, currency, mode, entries }, entries.map(entry => entry.memberId));
  const amounts = mode === "exact" ? entries.map(entry => rowMinor(entry.value))
    : distributeLargestRemainder(rowMinor(amountMinor), entries.map(entry => entry.value));
  return new Map(entries.map((entry, index) => [entry.memberId, amounts[index]!]));
}

/** Convert the total once, then allocate by native shares to preserve the base total exactly. */
function baseAllocation(expense: SplitExpenseInput, baseCurrency: string): { shares: Map<string, Minor>; total: Minor } {
  const shares = computeShares(expense.amountMinor, expense.mode, expense.entries, expense.currency);
  const baseTotal = rowConversion(rowMinor(expense.amountMinor), expense.currency, baseCurrency, expense.rateNano);
  const nativeAmounts = [...shares.values()];
  // A zero native total has no positive weights; its validated conversion is also zero.
  const baseAmounts = expense.amountMinor === 0 ? nativeAmounts
    : distributeLargestRemainder(baseTotal, nativeAmounts);
  return { shares: new Map([...shares.keys()].map((id, index) => [id, baseAmounts[index]!])), total: baseTotal };
}

export function toBaseShares(expense: SplitExpenseInput, baseCurrency: string): Map<string, Minor> {
  return baseAllocation(expense, baseCurrency).shares;
}

function memberBalance(balances: ReadonlyMap<string, number>, id: string): Minor {
  const value = balances.get(id);
  if (value === undefined) throw new SplitRowError(`Member ${id} is not a group member.`);
  return rowMinor(value);
}

/** Exact zero-sum assertion by cancellation; combined creditors may exceed the safe range. */
export function assertZeroSum(balances: ReadonlyMap<string, number>): void {
  const credits: number[] = [];
  const debts: number[] = [];
  for (const value of balances.values()) {
    minor(value);
    if (value > 0) credits.push(value);
    if (value < 0) debts.push(-value);
  }
  let creditor = 0;
  let debtor = 0;
  while (creditor < credits.length && debtor < debts.length) {
    const amount = Math.min(credits[creditor]!, debts[debtor]!);
    credits[creditor] = credits[creditor]! - amount;
    debts[debtor] = debts[debtor]! - amount;
    if (credits[creditor] === 0) creditor++;
    if (debts[debtor] === 0) debtor++;
  }
  if (creditor !== credits.length || debtor !== debts.length) {
    throw new Error("Balances must sum to 0 in base minor units; check expenses and transfers.");
  }
}

export interface SkippedSplitRow {
  kind: "expense" | "transfer";
  id: string;
  reason: string;
}

export interface SplitBalanceResult {
  balances: Map<string, Minor>;
  skipped: SkippedSplitRow[];
}

function assertMember(memberIds: readonly string[], id: string): void {
  if (!memberIds.includes(id)) throw new SplitRowError(`Member ${id} is not a group member.`);
}

function assertExpense(expense: SplitExpenseInput, memberIds: readonly string[]): void {
  if (expense.occurredAt !== undefined) assertSplitOccurredAt(expense.occurredAt);
  assertMember(memberIds, expense.payerMemberId);
  assertSplit(expense, memberIds);
}

/** Check every affected balance before committing this independently zero-sum row. */
function applyRow(balances: Map<string, Minor>, deltas: ReadonlyMap<string, number>): void {
  assertZeroSum(deltas);
  const next = new Map<string, Minor>();
  for (const [id, delta] of deltas) next.set(id, rowMinor(memberBalance(balances, id) + delta));
  for (const [id, balance] of next) balances.set(id, balance);
}

/** Retain accepted row references so summaries include exactly the rows behind their balances. */
function countedBalances<E extends SplitExpenseInput>(
  group: SplitGroupInput,
  expenses: readonly E[],
  transfers: readonly SplitTransferInput[],
): SplitBalanceResult & { expenses: E[]; transfers: SplitTransferInput[] } {
  exponentOf(group.baseCurrency);
  const balances = new Map<string, Minor>();
  for (const member of group.members) {
    if (balances.has(member.id)) throw new Error(`Duplicate group member id: ${member.id}.`);
    balances.set(member.id, minor(0));
  }
  const memberIds = [...balances.keys()];
  const skipped: SkippedSplitRow[] = [];
  const countedExpenses: E[] = [];
  const countedTransfers: SplitTransferInput[] = [];
  for (const [index, expense] of expenses.entries()) {
    try {
      assertExpense(expense, memberIds);
      const { shares, total: baseTotal } = baseAllocation(expense, group.baseCurrency);
      const payerOwed = shares.get(expense.payerMemberId) ?? minor(0);
      const deltas = new Map<string, number>([[expense.payerMemberId, baseTotal - payerOwed]]);
      for (const [id, share] of shares) {
        if (id !== expense.payerMemberId) deltas.set(id, 0 - share);
      }
      applyRow(balances, deltas);
      countedExpenses.push(expense);
    } catch (error) {
      if (!(error instanceof SplitRowError)) throw error;
      skipped.push({ kind: "expense", id: expense.id ?? String(index), reason: error.message });
    }
  }
  for (const [index, transfer] of transfers.entries()) {
    try {
      if (transfer.occurredAt !== undefined) assertSplitOccurredAt(transfer.occurredAt);
      const result = validateTransfer({ ...transfer, memberIds });
      if (!result.ok) throw new SplitRowError(`${result.reason}: ${result.detail}`);
      applyRow(balances, new Map([[transfer.from, transfer.amountMinor], [transfer.to, -transfer.amountMinor]]));
      countedTransfers.push(transfer);
    } catch (error) {
      if (!(error instanceof SplitRowError)) throw error;
      skipped.push({ kind: "transfer", id: transfer.id ?? String(index), reason: error.message });
    }
  }
  assertZeroSum(balances);
  return { balances, skipped, expenses: countedExpenses, transfers: countedTransfers };
}

/** Live rows for one group only. Positive = owed money; negative = owes money. */
export function computeBalances(
  group: SplitGroupInput,
  expenses: readonly SplitExpenseInput[],
  transfers: readonly SplitTransferInput[],
): SplitBalanceResult {
  const { balances, skipped } = countedBalances(group, expenses, transfers);
  return { balances, skipped };
}

interface BalanceMagnitude { id: string; amount: number }

/** Code-point id order is independent of runtime locale. */
function compareIds(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

function largestFirst(a: BalanceMagnitude, b: BalanceMagnitude): number {
  return b.amount - a.amount || compareIds(a.id, b.id);
}

/** Greedy largest remaining creditor/debtor; ties by ascending member id. */
export function settle(balances: ReadonlyMap<string, number>): SettlementTransfer[] {
  assertZeroSum(balances);
  const creditors: BalanceMagnitude[] = [];
  const debtors: BalanceMagnitude[] = [];
  for (const [id, value] of balances) {
    if (value > 0) creditors.push({ id, amount: value });
    if (value < 0) debtors.push({ id, amount: -value });
  }
  const transfers: SettlementTransfer[] = [];
  while (creditors.length > 0 && debtors.length > 0) {
    creditors.sort(largestFirst);
    debtors.sort(largestFirst);
    const creditor = creditors[0]!;
    const debtor = debtors[0]!;
    const amount = Math.min(creditor.amount, debtor.amount);
    transfers.push({ from: debtor.id, to: creditor.id, amountMinor: minor(amount) });
    creditor.amount -= amount;
    debtor.amount -= amount;
    if (creditor.amount === 0) creditors.shift();
    if (debtor.amount === 0) debtors.shift();
  }
  return transfers;
}

/** Native-home identity takes priority over every FX path, preventing a lossy round trip. */
export function myShareHome(
  expense: SplitExpenseInput,
  meMemberId: string,
  homeCurrency: string,
  homeRateNano: RateNano | null,
  baseCurrency: string,
  memberIds: readonly string[],
): Cents {
  assertMember(memberIds, meMemberId);
  assertExpense(expense, memberIds);
  if (!isCurrencyCode(homeCurrency)) throw new SplitRowError(`Unsupported currency ${homeCurrency}. Choose a supported two-decimal currency in Settings before importing your share.`);
  if (exponentOf(homeCurrency) !== HOME_CURRENCY_EXPONENT) throw new SplitRowError("Home ledgers require a two-decimal currency. Choose one in Settings before importing your share.");
  if (expense.currency === homeCurrency) {
    const shares = computeShares(expense.amountMinor, expense.mode, expense.entries, expense.currency);
    return minorAsCents(shares.get(meMemberId) ?? minor(0), homeCurrency);
  }
  const baseShare = toBaseShares(expense, baseCurrency).get(meMemberId) ?? minor(0);
  if (baseCurrency === homeCurrency) return minorAsCents(baseShare, homeCurrency);
  if (homeRateNano === null) throw new MissingHomeRateError(baseCurrency, homeCurrency);
  return minorAsCents(rowConversion(baseShare, baseCurrency, homeCurrency, homeRateNano), homeCurrency);
}

/** Plain text for sharing: original currency totals, current balances, suggestions and payments. */
export function formatSettlementSummary(
  group: SplitGroupInput,
  expenses: readonly SplitSummaryExpenseInput[],
  transfers: readonly SplitTransferInput[],
  locale: string,
  timeZone = "UTC",
): string {
  const counted = countedBalances(group, expenses, transfers);
  const dateFormatter = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" });
  const localDate = (instant: string): string => {
    const parts = dateFormatter.formatToParts(new Date(instant));
    const part = (type: Intl.DateTimeFormatPartTypes): string => parts.find(value => value.type === type)!.value;
    return `${part("year")}-${part("month")}-${part("day")}`;
  };
  const { balances, skipped } = counted;
  const names = new Map(group.members.map(member => [member.id, member.name]));
  const name = (id: string): string => {
    const memberName = names.get(id);
    if (memberName === undefined) throw new Error(`Member ${id} is not a group member.`);
    return memberName;
  };
  const totals = new Map<string, Minor>();
  for (const expense of counted.expenses) {
    totals.set(expense.currency, minor((totals.get(expense.currency) ?? 0) + expense.amountMinor));
  }
  const lines = [group.name, `Base currency: ${group.baseCurrency}`];
  const skippedExpenses = skipped.filter(row => row.kind === "expense").length;
  const skippedTransfers = skipped.filter(row => row.kind === "transfer").length;
  if (skippedExpenses > 0) lines.push(`${String(skippedExpenses)} ${skippedExpenses === 1 ? "expense" : "expenses"} could not be counted — open the group to fix it`);
  if (skippedTransfers > 0) lines.push(`${String(skippedTransfers)} ${skippedTransfers === 1 ? "transfer" : "transfers"} could not be counted — open the group to fix it`);
  lines.push("", "Totals per currency");
  if (totals.size === 0) lines.push("  (none)");
  for (const [currency, amount] of [...totals].sort(([a], [b]) => compareIds(a, b))) {
    lines.push(`  ${currency}: ${formatMinor(amount, currency, locale)}`);
  }
  lines.push("", `Expenses (${String(counted.expenses.length)})`);
  if (counted.expenses.length === 0) lines.push("  (none)");
  for (const expense of [...counted.expenses].sort((a, b) => Date.parse(a.occurredAt) - Date.parse(b.occurredAt) || compareIds(a.occurredAt, b.occurredAt))) {
    lines.push(`  ${localDate(expense.occurredAt)} · ${name(expense.payerMemberId)} · ${expense.description.trim() || "(no description)"} — ${formatMinor(minor(expense.amountMinor), expense.currency, locale)}`);
  }
  lines.push("", "Balances");
  if (group.members.length === 0) lines.push("  (no members)");
  for (const [id, balance] of [...balances].sort(([aId, a], [bId, b]) => b - a || compareIds(aId, bId))) {
    lines.push(`  ${name(id)}: ${balance > 0 ? "+" : ""}${formatMinor(balance, group.baseCurrency, locale)}`);
  }
  lines.push("", skipped.length > 0 ? "Suggested settle-up (incomplete)" : "Suggested settle-up");
  const suggestions = settle(balances);
  if (suggestions.length === 0) lines.push("  All settled.");
  for (const transfer of suggestions) {
    lines.push(`  ${name(transfer.from)} → ${name(transfer.to)}: ${formatMinor(transfer.amountMinor, group.baseCurrency, locale)}`);
  }
  lines.push("", `Recorded transfers (${String(counted.transfers.length)})`);
  if (counted.transfers.length === 0) lines.push("  (none)");
  for (const transfer of counted.transfers) {
    lines.push(`  ${name(transfer.from)} → ${name(transfer.to)}: ${formatMinor(minor(transfer.amountMinor), group.baseCurrency, locale)}`);
  }
  return lines.join("\n");
}
